import React from 'react';
import { Mail, Phone, MapPin, ExternalLink } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';

const Contact = () => {
  const { t, language } = useLanguage();

  return (
    <div className="min-h-screen pt-24 pb-20">
      {/* Hero */}
      <section className="bg-[#141F48] text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">{t('contactTitle')}</h1>
          <p className="text-xl text-[#D3BC8D] font-medium">{t('contactSubtitle')}</p>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Details */}
            <div className="space-y-8">
              <div>
                <h2 className="text-3xl font-bold text-[#141F48] mb-6">
                  {language === 'en' ? 'Get In Touch' : 'تواصل معنا'}
                </h2>
                <p className="text-lg text-gray-700 leading-relaxed">
                  {language === 'en'
                    ? 'We are here to assist with your EPC project requirements. Contact us today to discuss how ALMAD can bring your vision to reality.'
                    : 'نحن هنا للمساعدة في متطلبات مشروع EPC الخاص بك. اتصل بنا اليوم لمناقشة كيف يمكن لـ ALMAD تحويل رؤيتك إلى واقع.'}
                </p>
              </div>

              <div className="space-y-6">
                <Card className="border-l-4 border-l-[#D3BC8D]">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-[#141F48] rounded-lg flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-6 h-6 text-[#D3BC8D]" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-[#141F48] mb-2">{t('address')}</h3>
                        <p className="text-gray-600">
                          King Abdulaziz Road Tower, 12th Floor, Office 1208<br />
                          Jeddah 23412, Kingdom of Saudi Arabia
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-l-[#D3BC8D]">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-[#141F48] rounded-lg flex items-center justify-center flex-shrink-0">
                        <Phone className="w-6 h-6 text-[#D3BC8D]" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-[#141F48] mb-2">{t('phone')}</h3>
                        <a href="tel:+966596817974" className="text-gray-600 hover:text-[#D3BC8D] transition-colors">
                          +966 (0) 596 817 974
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-l-[#D3BC8D]">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-[#141F48] rounded-lg flex items-center justify-center flex-shrink-0">
                        <Mail className="w-6 h-6 text-[#D3BC8D]" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-[#141F48] mb-2">{t('email')}</h3>
                        <a href="mailto:info@almad.com" className="text-gray-600 hover:text-[#D3BC8D] transition-colors">
                          info@almad.com
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div>
                <a
                  href="https://maps.app.goo.gl/6oNMct3mZiPRPHKz7"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button size="lg" className="bg-[#D3BC8D] text-[#141F48] hover:bg-[#c9b180] font-semibold">
                    {t('viewMap')}
                    <ExternalLink className="ml-2 w-5 h-5" />
                  </Button>
                </a>
              </div>
            </div>

            {/* Office Building Image */}
            <div className="relative">
              <div className="sticky top-24">
                <Card className="overflow-hidden shadow-xl">
                  <div className="relative">
                    <img
                      src="/assets/images/office-tower.jpg"
                      alt="King Abdulaziz Road Tower - ALMAD Office Building in Jeddah"
                      className="w-full h-[400px] object-cover"
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#141F48]/95 via-[#141F48]/60 to-transparent p-6 pb-4">
                      <h3 className="text-white font-bold text-lg mb-1">
                        {language === 'en' ? 'King Abdulaziz Road Tower' : 'برج طريق الملك عبد العزيز'}
                      </h3>
                      <p className="text-[#D3BC8D] text-sm">
                        {language === 'en' ? 'Our Office in Jeddah, KSA' : 'مكتبنا في جدة، المملكة العربية السعودية'}
                      </p>
                    </div>
                  </div>
                </Card>
                <Card className="mt-6 border-2 border-[#D3BC8D] bg-white">
                  <CardContent className="p-6 bg-white">
                    <h3 className="text-xl font-bold text-[#141F48] mb-4">
                      {language === 'en' ? 'Business Hours' : 'ساعات العمل'}
                    </h3>
                    <div className="space-y-3 text-gray-800 font-medium">
                      <p>{language === 'en' ? 'Sunday - Thursday: 8:00 AM - 5:00 PM' : 'الأحد - الخميس: 8:00 ص - 5:00 م'}</p>
                      <p>{language === 'en' ? 'Friday - Saturday: Closed' : 'الجمعة - السبت: مغلق'}</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PEP App CTA */}
      <section className="py-16 bg-[#D3BC8D]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[#141F48] mb-4">
            {t('pepTitle')}
          </h2>
          <p className="text-lg text-[#141F48]/80 mb-8">
            {t('pepText')}
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <a href="https://raci-procedures.emergent.host/auth" target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="bg-[#141F48] text-white hover:bg-[#0f1a3d] font-semibold">
                {t('openPepApp')}
                <ExternalLink className="ml-2 w-5 h-5" />
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;